import { createContext, useEffect, useMemo, useReducer } from 'react';
import type { ReactNode } from 'react';

import { useNavigate } from 'react-router-dom';
import { Device } from '@capacitor/device';
import * as Sentry from '@sentry/react';

// @ts-expect-error — legacy JS module
import useSettings from '../hooks/useSettings';

// @ts-expect-error — legacy JS module
import { onLogout } from '../redux/slices/app';
// @ts-expect-error — legacy JS module
import { clearVacationId } from '../redux/slices/dashboard';
// @ts-expect-error — legacy JS module
import { useDispatch } from '../redux/store';

import { PATH_AUTH, PATH_PAGE } from '../routes/paths';
// @ts-expect-error — legacy JS module
import track from '../utils/analytics';
// @ts-expect-error — legacy JS module
import { deleteCredentials, setCredentials } from '../utils/biometric';
// @ts-expect-error — legacy JS module
import { isValidToken, setSession } from '../utils/jwt';
// @ts-expect-error — legacy JS module
import * as notif from '../utils/notifications';

import socket from '../utils/socket';

// @ts-expect-error — legacy JS module
import getFeatures from './FeaturesContext';

import { useAdminStore } from '../store/useAdminStore';
import { getMyPermissionsKey } from '../hooks/api/usePermissions';
import { clearQueryCache, queryClient } from '../providers/queryClient';

import type { AuthContextValue, AuthUser, LoginOptions } from '../types/auth';
import type { SocketInitResult } from '../types/socket';

import { authReducer, initialAuthState } from './authReducer';
import { computeUserShortcuts } from './computeUserShortcuts';

// =========================================================================
// Context
// =========================================================================

const defaultContextValue: AuthContextValue = {
  ...initialAuthState,
  hasClientRole: false,
  hasManagerRole: false,
  hasSmsProvider: false,
  hasVoiceProvider: false,
  isClient: false,
  isCometeOnTime: false,
  isManager: false,
  isMultiClient: false,
  isPartner: false,
  isResource: false,
  isSubClient: false,
  cancelDisconnection: () => {},
  disconnect: () => {},
  reconnect: () => {},
  get: () => Promise.resolve({} as never),
  initSocket: () => Promise.resolve({} as never),
  login: () => Promise.resolve(),
  logout: () => {},
  method: 'jwt',
  on: () => {},
  post: () => Promise.resolve({} as never),
  put: () => Promise.resolve({} as never),
  removeApiCode: () => [],
  resetPassword: () => Promise.resolve({} as never),
  sendResetLink: () => Promise.resolve({} as never),
  setUser: () => {}
};

const AuthContext = createContext<AuthContextValue>(defaultContextValue);

// =========================================================================
// Provider
// =========================================================================

function AuthProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(authReducer, initialAuthState);
  const dispatchFromRedux = useDispatch();
  const navigate = useNavigate();
  const { onChangeColor } = useSettings();

  // -----------------------------------------------------------------------
  // Role shortcuts
  // -----------------------------------------------------------------------

  const { user = {} as AuthUser } = state;
  const role = user?.role ?? '';
  const {
    hasClientRole,
    hasManagerRole,
    hasSmsProvider,
    hasVoiceProvider,
    isClient,
    isCometeOnTime,
    isManager,
    isMultiClient,
    isPartner,
    isResource,
    isSubClient
  } = computeUserShortcuts(role, user ?? {});

  // -----------------------------------------------------------------------
  // Notifications
  // -----------------------------------------------------------------------

  const handleNotif = () => {
    notif.onReceived((notification: unknown) => console.log('Notification received', notification));

    notif.addListeners({
      onRegister: (token: string) => {
        Device.getInfo()
          .then((deviceInfo) => {
            socket
              .request('POST', '/notifications/registerToken', {
                token,
                device: `${deviceInfo.manufacturer} ${deviceInfo.name}`,
                clientCode: state.env.parameters?.client_code,
                userId: user?.id,
                platform: window.env.isAndroid ? 1 : 0
              })
              .catch(console.error);
          })
          .catch(track.exception);
      }
    });

    notif.onActionPerformed(async (notification: { data?: Record<string, string>; extra?: Record<string, string> }) => {
      const data = notification.data || notification.extra;
      if (!data) return;

      let urlNotifRedirect = '';
      if (data.message) {
        urlNotifRedirect = `${PATH_PAGE.resource.root}`;
      }
      if (data.newId) {
        urlNotifRedirect = `${(PATH_PAGE as unknown as Record<string, { root?: string }>)[data.role]?.root}?newId=${data.newId}`;
      }
      if (data.vacationId) {
        if (data.role === 'manager') {
          urlNotifRedirect = `${PATH_PAGE.general.services}?vacationId=${data.vacationId}`;
        } else {
          urlNotifRedirect = `${PATH_PAGE.resource.root}?vacationId=${data.vacationId}`;
        }
      }
      if (data.demandId) {
        urlNotifRedirect = `${PATH_PAGE.general.demand}?demandId=${data.demandId}`;
      }
      if (data.demandPartnerId) {
        urlNotifRedirect = `${PATH_PAGE.manager_partner.partnerDemands}?id=${data.demandPartnerId}`;
      }
      if (data.scheduleMonth) {
        urlNotifRedirect = `${PATH_PAGE.resource.schedule}?month=${data.scheduleMonth}`;
      }
      if (data.missionId) {
        urlNotifRedirect = `${PATH_PAGE.resource.missions}?missionId=${data.missionId}`;
      }
      if (data.unavailabilityId) {
        urlNotifRedirect = data.vacationId
          ? `${PATH_PAGE.resource.root}?vacationId=${data.vacationId}`
          : `${PATH_PAGE.resource.root}`;
      }

      if (data.fileId && data.type && data.role) {
        sessionStorage.setItem('fileToOpen', data.fileId);

        switch (data.type) {
          case 'new_doc_agent':
          case 'validate_doc_agent':
            if (data.resourceId) {
              urlNotifRedirect =
                data.role === 'resource'
                  ? `${PATH_PAGE.resource.sheet}`
                  : `${PATH_PAGE.general.resource.resourceDocuments}?targetType=resource&targetId=${data.resourceId}`;
            }
            break;

          case 'new_doc_site':
          case 'validate_doc_site':
            urlNotifRedirect =
              data.role === 'resource'
                ? `${PATH_PAGE.resource.schedule}`
                : `${PATH_PAGE.general.site.siteSchedule}?siteId=0`;
            break;
        }
      }

      sessionStorage.setItem('urlNotifRedirect', urlNotifRedirect);

      if (data.apiCode !== socket.apiCode) {
        initialize(data.apiCode).then(() => {
          const storedUrl = sessionStorage.getItem('urlNotifRedirect');
          navigate(storedUrl || '/');
          sessionStorage.removeItem('urlNotifRedirect');
        });
      } else if (socket.user) {
        navigate(urlNotifRedirect);
      } else {
        navigate(urlNotifRedirect);
      }
    });
  };

  // -----------------------------------------------------------------------
  // Initialize
  // -----------------------------------------------------------------------

  const initialize = (apiCode?: string): Promise<SocketInitResult> =>
    new Promise((resolve, reject) => {
      socket
        .initSocket(apiCode)
        .then((initSocketResult) => {
          const {
            sockets,
            socketUrl,
            apiVersion,
            apiUrl,
            env,
            user: socketUser,
            accessToken,
            isDevicePaired
          } = initSocketResult;

          if (accessToken && isValidToken(accessToken)) {
            setSession(accessToken);
          }

          const { color_primary: primary, color_secondary: secondary } = (env.settings ?? {}) as Record<
            string,
            unknown
          >;

          if (env.settings) {
            if (Number.isInteger(primary)) {
              localStorage.setItem('color_primary', String(primary));
            }
            if (Number.isInteger(secondary)) {
              localStorage.setItem('color_secondary', String(secondary));
            }
            onChangeColor({ target: { value: 'custom' } });
          }

          const isAuthenticated = !!Object.keys(socketUser).length;
          if (isAuthenticated) {
            Sentry.setUser({
              id: (socketUser as AuthUser).id,
              username: (socketUser as AuthUser).username,
              email: (socketUser as AuthUser).email,
              role: (socketUser as AuthUser).role,
              uuid: window.env.uuid
            });
          }

          dispatch({
            type: 'INITIALIZE',
            payload: {
              isAuthenticated,
              isDevicePaired: isDevicePaired ?? false,
              sockets,
              socketUrl,
              apiVersion,
              apiCode,
              apiUrl,
              features: getFeatures(apiVersion),
              env,
              user: socketUser as AuthUser
            }
          });
          resolve(initSocketResult);
        })
        .catch((err: unknown) => {
          console.error("Erreur d'initialisation du socket:", err);
          dispatch({
            type: 'INITIALIZE',
            payload: {
              isAuthenticated: false,
              isDevicePaired: false,
              sockets: initialAuthState.sockets,
              socketUrl: null,
              apiVersion: null,
              apiCode: initialAuthState.apiCode,
              apiUrl: null,
              features: initialAuthState.features,
              env: initialAuthState.env,
              user: null
            }
          });
          reject(err);

          if (err instanceof Error && err.message === 'maintenance') {
            navigate('/maintenance');
          }
        });
    });

  // -----------------------------------------------------------------------
  // Effect — mount
  // -----------------------------------------------------------------------

  useEffect(() => {
    initialize(initialAuthState.apiCode).catch(console.error);
    if (!window.env.isClockingApp) {
      handleNotif();
    }
  }, []);

  // -----------------------------------------------------------------------
  // Methods
  // -----------------------------------------------------------------------

  const initSocket = (apiCode?: string) => initialize(apiCode);

  const disconnect = () => {
    socket.disconnect();
    dispatch({ type: 'LOGOUT', payload: { hasLogout: false, socketUrl: null } });
    dispatchFromRedux(onLogout());
    dispatchFromRedux(clearVacationId());
  };

  const cancelDisconnection = () => socket.cancelDisconnection();

  const reconnect = () => socket.reconnect();

  const removeApiCode = (apiCode: string) => socket.removeApiCode(apiCode);

  /**
   * Connexion unifiée : par email/mot de passe (ou switch multi-client via `id`),
   * ou par code agent 5 chiffres (`code`, pointeuse MCE). ApiService rejette si
   * la réponse ne contient pas d'accessToken/user — pas de garde-fou nécessaire ici.
   */
  const login = async ({ email, password, id = null, code, redirect = true }: LoginOptions = {}) => {
    const {
      socketUrl,
      accessToken,
      apiUrl,
      isDevicePaired,
      user: loggedUser,
      permissions
    } = code ? await socket.loginByCode(code) : await socket.login(email!, password!, id);

    setSession(accessToken);

    if (!code && window.env.isApp && !window.env.isClockingApp) {
      deleteCredentials(socketUrl)
        .then(() => {
          setCredentials(socketUrl, email, password).catch(track.exception);
        })
        .catch(track.exception);

      notif.register();
    }

    // CW-21903 : pré-remplit store + cache pour éviter le fetch
    // /mce/my-permissions séparé (cause du délai du bloc MCE au switch
    // d'espace). Le guard `length > 0` est critique : tableau vide = back
    // a échoué côté webservice, on laisse useMyPermissions refetch.
    const userId = (loggedUser as AuthUser)?.id;
    const userRole = (loggedUser as AuthUser)?.role;
    if (Array.isArray(permissions) && permissions.length > 0 && userId) {
      useAdminStore.getState().setPermissions(permissions, userId, userRole);
      queryClient.setQueryData(getMyPermissionsKey(userId), permissions);
    }

    dispatch({
      type: 'LOGIN',
      payload: {
        socketUrl: socketUrl ?? null,
        isDevicePaired: !!isDevicePaired,
        apiUrl: apiUrl ?? null,
        user: loggedUser as AuthUser
      }
    });

    if (redirect) {
      const urlNotifRedirect = sessionStorage.getItem('urlNotifRedirect');
      navigate(urlNotifRedirect || '/');
      sessionStorage.removeItem('urlNotifRedirect');
    }

    Sentry.setUser({
      id: userId,
      username: (loggedUser as AuthUser)?.username,
      email,
      role: userRole,
      uuid: window.env.uuid
    });

    track.setUser(loggedUser);
    track.event('login', { method: code ? 'code' : email || id ? 'email' : 'auto' });
  };

  const logout = (isNavigate = true) => {
    socket.logout();
    setSession(null);
    // CW-22023 : vide le cache React Query (memoire + localStorage persiste).
    // Sans ce purge, les queries scoped par userId du user qui se deconnecte
    // restent persistees (24 h via comete-link-react-query). Sur device
    // partage, chaque user laisse ses permissions/data lisibles en
    // localStorage et le cache enfle a chaque session.
    clearQueryCache();
    dispatchFromRedux(onLogout());
    dispatchFromRedux(clearVacationId());
    track.setUser(null);
    dispatch({ type: 'LOGOUT', payload: { hasLogout: true, socketUrl: state.socketUrl ?? null } });
    if (isNavigate) {
      navigate(window.env.isClockingApp ? PATH_AUTH.clockingApp.login : PATH_AUTH.login);
    }
  };

  const setUser = (updatedUser: AuthUser) => dispatch({ type: 'SET_USER', payload: { user: updatedUser } });

  const sendResetLink = (email: string) => socket.request('POST', '/sendResetLink', { email });

  const resetPassword = (token: string, password: string) =>
    socket.request('POST', '/resetPassword', { token, password });

  const on = (eventKey: string, callback: (...args: unknown[]) => void) => socket.on(eventKey, callback);

  const get = (route: string, props = {}) => socket.request('GET', route, props);

  const post = (route: string, props = {}) => socket.request('POST', route, props);

  const put = (route: string, props = {}) => socket.request('PUT', route, props);

  // -----------------------------------------------------------------------
  // Context value — memoized to avoid re-rendering 176 consumers on every dispatch
  // -----------------------------------------------------------------------

  const contextValue = useMemo<AuthContextValue>(
    () => ({
      ...state,
      setUser,
      cancelDisconnection,
      disconnect,
      reconnect,
      get,
      hasClientRole,
      hasManagerRole,
      hasSmsProvider,
      hasVoiceProvider,
      initSocket,
      isClient,
      isCometeOnTime,
      isManager,
      isMultiClient,
      isPartner,
      isResource,
      isSubClient,
      login,
      logout,
      method: 'jwt',
      on,
      post,
      put,
      removeApiCode,
      resetPassword,
      sendResetLink
    }),

    [state]
  );

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
}

export { AuthContext, AuthProvider };
